import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, FileText, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

export default function PrivacyPolicy() {
  return (
    <div className="pb-24 bg-stone-50/50">
      <SEO 
        title="Privatlivspolitik | Vejen til barnet"
        description="Læs privatlivspolitikken for Vejen til barnet. Se, hvordan vi behandler og beskytter dine personoplysninger i overensstemmelse med gældende GDPR-regler."
        path="/privatlivspolitik"
      />
      {/* Header Section */}
      <section className="relative py-20 bg-[#4e6354] text-white overflow-hidden">
        {/* Watermark Overlay - Winding Road */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.08] flex items-start justify-center">
          <img 
            src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/95574613-b9ad-4e75-84fb-52de2d444fa1.png" 
            alt="" 
            className="w-full h-full object-cover scale-150 rotate-6 invert brightness-200 object-top"
          />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-stone-200 hover:text-white transition-colors text-sm uppercase tracking-wider mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Tilbage til forsiden
          </Link>
          <motion.h1 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-5xl font-light uppercase tracking-tight mb-4"
          >
            Privatlivspolitik
          </motion.h1>
          <p className="text-stone-200 max-w-xl mx-auto font-light">
            Information om hvordan jeg passer på dine personlige oplysninger og respekterer dit privatliv.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl p-8 md:p-12 border border-stone-200/40 shadow-sm space-y-10"
        >
          {/* Intro Card */}
          <div className="flex flex-col sm:flex-row gap-6 items-start pb-8 border-b border-stone-100">
            <div className="w-12 h-12 rounded-2xl bg-[#4e6354]/10 flex items-center justify-center text-[#4e6354] shrink-0">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-stone-900 mb-2">Dit privatliv er vigtigt for mig</h2>
              <p className="text-stone-600 leading-relaxed">
                Når du henvender dig til mig angående pædagogiske oplæg, Theraplay eller Marte Meo-forløb, betror du mig med dine oplysninger. Denne privatlivspolitik beskriver, hvordan jeg indsamler, beskytter og anvender dine data i overensstemmelse med EU's databeskyttelsesforordning (GDPR).
              </p>
            </div>
          </div>

          {/* Section 1: Dataansvarlig */}
          <div>
            <h3 className="text-lg font-bold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-1.5 h-6 bg-[#4e6354] rounded-full"></span>
              1. Dataansvarlig
            </h3>
            <p className="text-stone-600 leading-relaxed mb-4">
              Den dataansvarlige for behandlingen af dine personoplysninger er:
            </p>
            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200/30 max-w-md">
              <p className="font-semibold text-stone-900">Anette Lundager Schädler</p>
              <p className="text-stone-600">Højskolevej 1</p>
              <p className="text-stone-600">6000 Kolding</p>
              <p className="text-stone-600 mt-2">
                Tlf: <a href="tel:+4520487080" className="text-[#4e6354] hover:underline">+45 20 48 70 80</a>
              </p>
              <p className="text-stone-600">
                E-mail: <a href="mailto:vejentilbarnet@gmail.com" className="text-[#4e6354] hover:underline">vejentilbarnet@gmail.com</a>
              </p>
            </div>
          </div>

          {/* Section 2: Indsamling af oplysninger */}
          <div>
            <h3 className="text-lg font-bold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-1.5 h-6 bg-[#4e6354] rounded-full"></span>
              2. Hvilke oplysninger indsamles og hvorfor?
            </h3>
            <p className="text-stone-600 leading-relaxed mb-4">
              Jeg behandler kun de personoplysninger, som du selv giver mig i forbindelse med en henvendelse eller etablering af et forløb. Det drejer sig typisk om:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-stone-600 mb-4">
              <li><strong>Kontaktoplysninger:</strong> Navn, e-mailadresse, eventuelt telefonnummer og adresse.</li>
              <li><strong>Henvendelsesdata:</strong> De oplysninger, du selv vælger at skrive i kontaktformularen eller sende via e-mail (f.eks. beskrivelse af dit barns trivsel eller jeres hverdagsudfordringer).</li>
              <li><strong>Forløbsdata:</strong> I forbindelse med et konkret pædagogisk oplæg-, Theraplay- eller Marte Meo-forløb behandles relevante oplysninger om samspil og udviklingsmål. For Marte Meo-forløb indgår videooptagelser af samspilssituationer, som udelukkende optages og anvendes efter særskilt skriftligt samtykke.</li>
            </ul>
          </div>

          {/* Section 3: Formål og retsgrundlag */}
          <div>
            <h3 className="text-lg font-bold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-1.5 h-6 bg-[#4e6354] rounded-full"></span>
              3. Formål og retsgrundlag for behandlingen
            </h3>
            <p className="text-stone-600 leading-relaxed mb-4">
              Behandlingen af dine personoplysninger sker med følgende formål:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-stone-600">
              <li>At kunne besvare dine henvendelser og yde rådgivning.</li>
              <li>At kunne planlægge og gennemføre pædagogiske oplæg, Theraplay- eller Marte Meo-forløb.</li>
              <li>At opfylde aftaler indgået med dig (f.eks. booking af et oplæg).</li>
            </ul>
            <p className="text-stone-600 leading-relaxed mt-4">
              Retsgrundlaget for behandlingen er primært dit <strong>samtykke</strong> (GDPR art. 6, stk. 1, litra a) samt opfyldelsen af en <strong>kontrakt/aftale</strong> (GDPR art. 6, stk. 1, litra b). Behandling af følsomme oplysninger (f.eks. helbredsoplysninger eller videooptagelser til Marte Meo) sker altid på baggrund af et udtrykkeligt, skriftligt samtykke.
            </p>
          </div>

          {/* Section 4: Opbevaring og sletning */}
          <div>
            <h3 className="text-lg font-bold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-1.5 h-6 bg-[#4e6354] rounded-full"></span>
              4. Opbevaring og sletning af data
            </h3>
            <p className="text-stone-600 leading-relaxed mb-4">
              Jeg opbevarer kun dine personoplysninger, så længe det er nødvendigt i forhold til de formål, de blev indsamlet til:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-stone-600">
              <li><strong>Henvendelser:</strong> Hvis din henvendelse ikke fører til et forløb eller samarbejde, slettes dine oplysninger og e-mails senest 6 måneder efter seneste kontakt.</li>
              <li><strong>Forløb:</strong> Oplysninger og journalnotater i forbindelse med et aktivt forløb slettes som udgangspunkt senest 1 år efter afslutningen af forløbet, medmindre andet aftales skriftligt.</li>
              <li><strong>Videooptagelser (Marte Meo):</strong> Videooptagelser slettes løbende og senest umiddelbart efter forløbets afslutning, medmindre der er givet samtykke til andet (f.eks. til undervisningsbrug).</li>
            </ul>
          </div>

          {/* Section 5: Videregivelse */}
          <div>
            <h3 className="text-lg font-bold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-1.5 h-6 bg-[#4e6354] rounded-full"></span>
              5. Videregivelse af personoplysninger
            </h3>
            <p className="text-stone-600 leading-relaxed">
              Dine personoplysninger videregives aldrig til tredjepart uden dit forudgående, udtrykkelige samtykke. Jeg sælger ikke dine data til reklamebureauer eller andre eksterne aktører.
            </p>
          </div>

          {/* Section 6: Cookies og sporing */}
          <div>
            <h3 className="text-lg font-bold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-1.5 h-6 bg-[#4e6354] rounded-full"></span>
              6. Cookies og sporing
            </h3>
            <p className="text-stone-600 leading-relaxed">
              Denne hjemmeside er designet med fokus på dit privatliv. Hjemmesiden anvender <strong>ikke</strong> cookies til sporing, markedsføring eller statistik, og der indsamles ingen personlige oplysninger om din færden på siden.
            </p>
          </div>

          {/* Section 7: Dine rettigheder */}
          <div>
            <h3 className="text-lg font-bold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-1.5 h-6 bg-[#4e6354] rounded-full"></span>
              7. Dine rettigheder
            </h3>
            <p className="text-stone-600 leading-relaxed mb-4">
              Du har efter databeskyttelsesforordningen en række rettigheder i forhold til min behandling af dine oplysninger:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-stone-600">
              <li><strong>Ret til indsigt:</strong> Du har ret til at få indsigt i de oplysninger, jeg behandler om dig.</li>
              <li><strong>Ret til berigtigelse:</strong> Du har ret til at få rettet eventuelle ukorrekte oplysninger om dig selv.</li>
              <li><strong>Ret til sletning ("retten til at blive glemt"):</strong> I særlige tilfælde har du ret til at få slettet oplysninger om dig, inden tidspunktet for min almindelige generelle sletning indtræffer.</li>
              <li><strong>Ret til at trække samtykke tilbage:</strong> Hvis min behandling af dine oplysninger er baseret på dit samtykke, har du til enhver tid ret til at trække dit samtykke tilbage. Dette kan du gøre ved at kontakte mig på e-mail.</li>
            </ul>
            <p className="text-stone-600 leading-relaxed mt-4">
              Hvis du ønsker at gøre brug af dine rettigheder, eller hvis du har spørgsmål til min behandling af dine data, er du altid velkommen til at kontakte mig på <a href="mailto:vejentilbarnet@gmail.com" className="text-[#4e6354] hover:underline font-medium">vejentilbarnet@gmail.com</a>.
            </p>
            <p className="text-stone-600 leading-relaxed mt-2">
              Du har ligeledes ret til at indgive en klage til Datatilsynet, hvis du er utilfreds med den måde, jeg behandler dine personoplysninger på. Du finder Datatilsynets kontaktoplysninger på <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-[#4e6354] hover:underline">www.datatilsynet.dk</a>.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
